import { Avatar } from '@/components/ui';
import { GrowthTag } from '@/components/GrowthTag';
import { formatBRLShort, formatPercent } from '@/lib/format';
import type { RankingEntry, RankingMetric } from '@/lib/types';
import { formatMetric } from './rankingFormat';
import { PositionDelta } from './PositionDelta';

/**
 * A classificação em tabela, com todos os números de cada membro — a visão de
 * quem confere. A coluna da métrica escolhida ganha a cor de destaque: é por
 * ela que a tabela está ordenada.
 */
export function RankingTable({ entries, metric }: { entries: RankingEntry[]; metric: RankingMetric }) {
  const tone = (column: RankingMetric) => (column === metric ? 'num right emphasis' : 'num right');

  return (
    <div className="table-wrap">
      <table className="tbl tbl-cards">
        <thead>
          <tr>
            <th style={{ width: 52 }}>#</th>
            <th>Membro</th>
            <th className="right">Vendido</th>
            <th className="right" title="Vendas pagas pelo link do membro (e a Kirvano): a nota da venda. Pedidos sem o código do membro e comprovantes anexados ficam fora">Pelo link</th>
            <th className="right">Comprov.</th>
            <th className="right">Ticket médio</th>
            <th className="right">Maior venda</th>
            <th className="right" title="Depositado nas alavancagens de boas-vindas">Alav. boas-vindas</th>
            <th className="right" title="Depositado nas alavancagens de evento">Alav. evento</th>
            <th className="right" title="Todo o depositado: os depósitos e as alavancagens">Depositado</th>
            <th className="right" title="Repasse puxado nos comprovantes de Repasse Live">Repasse Live</th>
            <th className="right" title="Todo o repasse puxado, das alavancagens e das lives">Repasse total</th>
            <th className="right">Part.</th>
            <th className="right">vs. anterior</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.member.id} className="row-hover">
              <td className="cell-pos">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span className="num display" style={{ fontSize: 14 }}>{entry.position}</span>
                  <PositionDelta change={entry.positionChange} />
                </span>
              </td>
              <td className="cell-main">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={entry.member.name} color={entry.member.avatarColor} photo={entry.member.photoUrl} size={24} />
                  <span style={{ fontWeight: 500 }}>{entry.member.name}</span>
                  {!entry.member.active ? <span className="chip">inativo</span> : null}
                </span>
              </td>
              <td className="num right cell-third" data-label="Vendido">{formatBRLShort(entry.soldTotal)}</td>
              {/* A venda ordena pelo link do membro: é esta coluna que leva o destaque. */}
              <td className={`${tone('sold')} cell-key`} style={{ fontWeight: 500 }} data-label="Pelo link">
                {entry.linkCount > 0 ? `${formatBRLShort(entry.linkTotal)} · ${entry.linkCount}` : <span className="muted">—</span>}
              </td>
              <td className={`${tone('count')} cell-third`} data-label="Comprov.">{entry.receiptCount}</td>
              <td className={`${tone('ticket')} cell-third`} data-label="Ticket médio">{formatBRLShort(entry.averageTicket)}</td>
              <td className="num right muted cell-hide-sm">{formatBRLShort(entry.largestSale)}</td>
              <td className={`${tone('welcome')} cell-third`} data-label="Alav. boas-vindas">{formatBRLShort(entry.welcomeTotal)}</td>
              <td className={`${tone('event')} cell-third`} data-label="Alav. evento">{formatBRLShort(entry.eventTotal)}</td>
              <td className={`${tone('deposit')} cell-third`} data-label="Depositado">{formatBRLShort(entry.depositTotal)}</td>
              <td className={`${tone('live')} cell-third`} data-label="Repasse Live">{formatBRLShort(entry.liveTotal)}</td>
              <td className={`${tone('transfer')} cell-third`} data-label="Repasse total">{formatBRLShort(entry.transferTotal)}</td>
              <td className="num right muted cell-third" data-label="Part.">{formatPercent(entry.share, 1)}</td>
              <td className="right cell-third" data-label="vs. anterior">
                <GrowthTag growth={entry.growth} previousLabel={formatMetric(metric, entry.previousScore)} compact />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
